// Session management utilities
//
// SECURITY: The session token is NEVER stored in localStorage on web.
// It is held only in an httpOnly cookie set by the backend (XSS-safe).
// Only non-sensitive metadata (expiry, user profile) is stored locally.
//
// iOS native bridge: reads sessionToken directly from the login response
// (see App.js iosCallback handler) and hands it to the native keychain.
const SESSION_EXPIRES_KEY = 'hermes_session_expires';
const SESSION_USER_KEY = 'hermes_session_user';

/**
 * Store session metadata (expiry + user) in localStorage.
 * The actual session token is stored server-side in an httpOnly cookie.
 */
export function saveSession(sessionToken, expiresAt, user) {
  // sessionToken intentionally NOT stored — it lives in the httpOnly cookie
  localStorage.setItem(SESSION_EXPIRES_KEY, expiresAt.toString());
  localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
}

/**
 * Get session expiry from localStorage.
 */
export function getSessionExpiry() {
  const expires = localStorage.getItem(SESSION_EXPIRES_KEY);
  return expires ? parseInt(expires, 10) : null;
}

/**
 * Get stored user info from localStorage.
 */
export function getStoredUser() {
  const userStr = localStorage.getItem(SESSION_USER_KEY);
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

/**
 * Check if session metadata indicates a valid (unexpired) session.
 * The actual cookie validity is checked server-side on each request.
 */
export function isSessionValid() {
  const expires = getSessionExpiry();
  if (!expires) return false;
  return Date.now() < expires;
}

/**
 * Clear session metadata (cookie is cleared by backend on /auth/logout).
 */
export function clearSession() {
  localStorage.removeItem(SESSION_EXPIRES_KEY);
  localStorage.removeItem(SESSION_USER_KEY);
  // Legacy cleanup — older builds stored the token here
  localStorage.removeItem('hermes_session_token');
}

/**
 * Exchange Google ID token for a session.
 * Backend sets an httpOnly cookie; we only persist metadata locally.
 */
export async function loginWithGoogle(idToken) {
  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    saveSession(data.sessionToken, data.expiresAt, data.user);
    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/**
 * Logout — backend clears the cookie, we clear local metadata.
 */
export async function logout() {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout error:', error);
  }
  clearSession();
}

/**
 * Returns null — with httpOnly cookies, the browser auto-attaches auth.
 * Kept for API backwards-compat; callers use credentials: 'include' instead.
 */
export function getAuthHeader() {
  return null;
}

/**
 * Make authenticated API request — cookie is sent automatically.
 */
export async function authenticatedFetch(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
  });

  // Check for session refresh header
  if (response.headers.get('X-Session-Refreshed') === 'true') {
    const newExpiry = response.headers.get('X-Session-Expires');
    if (newExpiry) {
      localStorage.setItem(SESSION_EXPIRES_KEY, newExpiry);
    }
  }

  return response;
}
